import { Card, GeneralCameraOffIcon, Loader } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { Swiper } from 'swiper';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { isProduction } from '@/utility/isSSR';
import { TAutoRentalTile } from '@/utility/mapAutoSearchResultToTile';
import { ISearchResultTile } from '@/utility/mapSearchResultToTile';

import css from './ListingTileMedia.module.css';

type TListingTileMediaProps = {
  listingTile: ISearchResultTile | TAutoRentalTile;
  withPriority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
  showFirstImageOnly?: boolean;
  showSecondaryImageOnHover?: boolean;
  highQuality?: boolean;
  onSlideChange?: (nextIndex: number) => void;
  actionsLayer?: React.ReactNode;
  className?: string;
};

export const ListingTileMedia = ({
  listingTile: { loading, photoUrl, photoAlt, photoMetadata },
  withPriority = false,
  withFetchPriority,
  showFirstImageOnly = false,
  showSecondaryImageOnHover = false,
  highQuality,
  onSlideChange,
  actionsLayer,
  className,
}: TListingTileMediaProps) => {
  const { isAboveXXXL } = useBreakpointContext();
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const [userInteractedWithCarousel, setUserInteractedWithCarousel] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverImageIndex, setHoverImageIndex] = useState(1); // Default to second image

  const images = useMemo(() => (typeof photoUrl === 'string' ? [photoUrl] : photoUrl), [photoUrl]);

  // Find a suitable interior image for hover
  useEffect(() => {
    if (!images || images.length <= 1) return;

    // Default to second image if no interior images are found
    let interiorImageIndex = 1;

    // Check if we have photo metadata and try to find an interior image
    if (photoMetadata && photoMetadata.length > 1) {
      // Look for interior images by category or tags
      const interiorIndex = photoMetadata.findIndex((image, index) => {
        // Skip the first image as that's already shown by default
        if (index === 0) return false;

        // Check for interior categories/tags
        const isInterior = image.category === 'interior' || image.tags === 'interior' || false;

        return isInterior;
      });

      // If we found an interior image, use its index
      if (interiorIndex > 0) {
        interiorImageIndex = interiorIndex;
      }
    }

    setHoverImageIndex(interiorImageIndex);
  }, [images, photoMetadata]);

  // Update the swiper when hover state changes
  useEffect(() => {
    if (!swiperInstance || userInteractedWithCarousel || !images || images.length <= 1) {
      return;
    }

    // Only change slide if we're not showing the active image already
    const targetIndex = isHovering ? hoverImageIndex : 0;

    if (swiperInstance.activeIndex !== targetIndex) {
      swiperInstance.slideTo(targetIndex);
    }
  }, [isHovering, swiperInstance, userInteractedWithCarousel, images, hoverImageIndex]);

  useEffect(() => {
    if (!swiperInstance) return;

    const handleRealIndexChange = (swiper: Swiper) => {
      const newIndex = swiper.realIndex;
      onSlideChange?.(newIndex);

      if (!isHovering || newIndex !== hoverImageIndex) {
        setUserInteractedWithCarousel(true);
      }
    };

    swiperInstance.on('realIndexChange', handleRealIndexChange);

    return () => {
      swiperInstance.off('realIndexChange', handleRealIndexChange);
    };
  }, [swiperInstance, onSlideChange, isHovering, hoverImageIndex]);

  const handleMouseEnter = () => {
    if (!userInteractedWithCarousel && images && images.length > 1) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!userInteractedWithCarousel && images && images.length > 1) {
      setIsHovering(false);
    }
  };

  if (!loading && !showFirstImageOnly && images && images.length > 1) {
    return (
      <div
        className={className}
        {...(showSecondaryImageOnHover && {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        })}>
        <Card.Carousel
          ref={ref => setSwiperInstance(ref?.swiper)}
          content={
            // Show a maximum of 7 images
            images.slice(0, 7).map((imageUrl, index) => (
              <MediaImage
                // Some images in dev have the same url
                key={`${isProduction() ? 'image' : index}-${imageUrl}`}
                src={imageUrl}
                alt={index === 0 ? photoAlt : undefined}
                priority={index === 0 && withPriority}
                withFetchPriority={index === 0 ? withFetchPriority : undefined}
                highQuality={isAboveXXXL}
              />
            ))
          }
          className={css.carousel}>
          {actionsLayer}
        </Card.Carousel>
      </div>
    );
  }

  return (
    <Card.Media className={cn('bg-gray-200', className)}>
      {images?.[0] && (
        <MediaImage
          src={images?.[0]}
          alt={photoAlt}
          priority={withPriority}
          withFetchPriority={withFetchPriority}
          highQuality={highQuality || isAboveXXXL}
        />
      )}
      {!!loading && (
        <Loader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
      )}

      {actionsLayer}
    </Card.Media>
  );
};

type TMediaImageProps = {
  src: string;
  alt?: string;
  priority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
  highQuality?: boolean;
};

const MediaImage = ({ highQuality, ...props }: TMediaImageProps) => {
  return (
    <div className="relative w-full h-full">
      <ResponsiveImage
        cropMode="fill"
        role="presentation"
        sourceSizes={highQuality ? ['landscape572'] : ['landscape375']}
        className="object-cover object-center w-full h-full"
        showFallbackComponent
        customFallbackContent={
          <div className="flex items-center justify-center w-full h-full">
            <GeneralCameraOffIcon className="text-3xl" />
          </div>
        }
        {...props}
      />

      <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0.1)]" />
    </div>
  );
};
